/**
 * /dashboard/surveys/new — HR-admin create draft survey (G16).
 *
 * Two-step flow:
 *   1. This page renders the metadata-only form (NewSurveyForm).
 *   2. On success it navigates to /dashboard/surveys/$id where the
 *      question editor + activate button live (SurveyEditor).
 *
 * Backend gate (POST /api/v1/surveys) is isHRAdmin — we surface that
 * gracefully on read here rather than letting a non-HR user
 * encounter a confusing 403 on Submit.
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import { PageHeader } from "@/components/shared/page-header";
import { NewSurveyForm } from "@/components/surveys/survey-builder";
import { Button } from "@/components/ui/button";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/surveys/new")({
  component: NewSurveyRoute,
});

function NewSurveyRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { user } = useAuth();
  const isHR = isHRAdmin(user?.role);

  return (
    <>
      <PageHeader
        title="New Survey"
        description="Create a draft. You'll add questions and activate it on the next step."
        actions={
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/surveys" params={{ tenantSlug }}>
              <ArrowLeft aria-hidden className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />
      {!isHR ? (
        <Alert variant="destructive">
          <AlertTitle>HR-admin only</AlertTitle>
          <AlertDescription>
            Only HR managers and company admins can author surveys.
          </AlertDescription>
        </Alert>
      ) : (
        <NewSurveyForm />
      )}
    </>
  );
}
